/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * operatorStore.ts — authoritative runtime state and commands for operators.
 * It combines server events with client-only workspace actions, owns the
 * realtime client lifecycle, and exposes narrowly scoped mutations to React.
 * Visual presentation and durable backend persistence are outside this store.
 */

import { createStore } from 'zustand';
import type { OperatorState } from './eventReducer';
import { getInitialOperatorState, applyEvent } from './eventReducer';
import { reduceCopilot } from './slices/copilotSlice';
import type { ServerEnvelope } from '@/realtime/types';
import { OperatorWsClient } from '@/realtime/operatorWsClient';
import type { ConnectionState } from '@/realtime/operatorWsClient';
import {
  buildPresenceRegister,
  buildHintMarkUsed,
  buildHintMarkIrrelevant,
} from '@/realtime/commands';
import { sendFeedback } from '@/api/feedback';
import { appConfig, getOrCreateDeviceId, getOperatorId } from '@/config/env';
import { featureFlags } from '@/config/featureFlags';
import { getMetricsCollector } from '@/observability/metrics';
import type { CustomerCard, ScenarioFieldValue, WrapUpOutcome } from './slices/workspaceSlice';
import type { HighlightWidget } from './slices/copilotSlice';
import { initialCallState } from './slices/callSlice';
import { translateCurrent } from '@/i18n/catalog';

export interface ConnectOptions {
  operatorId?: string;
  deviceId?: string;
}

export interface OperatorStore extends OperatorState {
  connectionState: ConnectionState;
  connect: (url: string, options?: ConnectOptions) => void;
  disconnect: () => void;
  send: (envelope: Parameters<OperatorWsClient['send']>[0]) => void;
  applyServerEvent: (envelope: ServerEnvelope) => void;
  /** E4-S2: Set full customer card (L1+L2) when loaded from API */
  setCustomerCard: (card: CustomerCard | null) => void;
  /** E4-S2: Update a scenario field with provenance */
  setScenarioField: (field: string, value: string, source: ScenarioFieldValue['source']) => void;
  /** E4-S3: Set wrap-up outcome (post-call) */
  setWrapUpOutcome: (outcome: WrapUpOutcome | null) => void;
  /** E4-S3: Dismiss wrap-up and return to queue (reset call to idle, clear wrap-up state) */
  dismissWrapUp: () => void;
  /** E5-S1: Clear meeting conflict banner (e.g. after operator dismisses or confirms). */
  clearMeetingConflict: () => void;
  /** E5-S1: Confirm meeting draft (sets meetingConfirmedAt, clears conflict for UX). */
  confirmMeeting: () => void;
  /**
   * Dev/demo only: set call to ended and wrap-up required fields to show WrapUpPanel.
   * Use for testing without real call_ended event.
   */
  simulateWrapUpState: (params: {
    callId: string;
    endReason?: string;
    durationMs?: number;
    requiredFields?: string[];
  }) => void;
  /** Dev/demo only: set meeting conflict to show conflict banner in CalendarWidget. */
  simulateMeetingConflict: (params: { message?: string; alternatives?: string[] }) => void;
  /** E5-S2: Set address coordinates (for map pin). */
  setAddressCoords: (coords: { lat: number; lon: number } | null) => void;
  /** E5-S2: Clear address validation warning banner. */
  clearAddressWarning: () => void;
  /** Dev/demo only: set address warning to show banner in AddressWidget. */
  simulateAddressWarning: (params: { code?: string; message?: string }) => void;
  /** E6-S1/E6-S2 Dev/demo: inject copilot event (asr_partial, asr_final, hint_generated). */
  simulateCopilotEvent: (
    eventType: 'asr_partial' | 'asr_final' | 'hint_generated',
    payload: Record<string, unknown>
  ) => void;
  /** E6-S2: Mark hint as used (send command, dismiss in UI). */
  markHintUsed: (hintId: string) => void;
  /** E6-S2: Mark hint as irrelevant (send command, dismiss in UI). */
  markHintIrrelevant: (hintId: string) => void;
  /** E6-S3: Clear contextual highlight (e.g. on TTL expiry or operator action). */
  clearHighlight: () => void;
  /** E6-S3 Dev/demo: inject highlight_recommended for UI highlighting. */
  simulateHighlight: (params: {
    target_widget: HighlightWidget;
    level?: 'soft' | 'medium' | 'critical';
    reason?: string;
    ttl_ms?: number;
  }) => void;
  /** E3-S3: Set in-call mute (local UI; mic pipeline will respect when capturing). */
  setCallMuted: (muted: boolean) => void;
  /** E3-S3: Set in-call hold (local UI; backend hold command can be added later). */
  setCallHeld: (held: boolean) => void;
  /** E3-S3 T3: Set voice media connection state (from transport onState). */
  setVoiceConnectionState: (
    state: 'disconnected' | 'connecting' | 'starting' | 'active' | 'ended'
  ) => void;
  reset: () => void;
}

let clientInstance: OperatorWsClient | null = null;

function createOperatorStore() {
  return createStore<OperatorStore>((set, get) => ({
    ...getInitialOperatorState(),
    connectionState: 'disconnected',

    connect: (url: string, options?: ConnectOptions) => {
      get().disconnect();
      const operatorId = options?.operatorId ?? getOperatorId();
      const deviceId = options?.deviceId ?? getOrCreateDeviceId();
      const client = new OperatorWsClient({
        url,
        onConnectionState: (state) => {
          set((prev) => {
            if (state === 'connected' && prev.connectionState === 'reconnecting') {
              getMetricsCollector().reportReconnect();
            }
            return {
              ...prev,
              connectionState: state,
              // E2-S3: on (re)connect show "registering" until presence_snapshot arrives
              ...(state === 'connected'
                ? {
                    presence: {
                      ...prev.presence,
                      status: 'registering',
                    },
                    // E2-S3 T2: re-sync — clear stale call/voice state after reconnect
                    // until server sends fresh events (presence_snapshot, call_offered, etc.)
                    call: initialCallState,
                    callMuted: false,
                    callHeld: false,
                    voiceConnectionState: 'disconnected' as const,
                  }
                : {}),
            };
          });
          if (state === 'connected') {
            const envelope = buildPresenceRegister({
              operator_id: operatorId,
              device_id: deviceId,
              client_version: appConfig.clientVersion,
              preferred_transport: featureFlags.callTransport === 'webrtc' ? 'webrtc' : 'websocket',
              cohort_id: featureFlags.canaryCohort,
            });
            clientInstance?.send(envelope);
          }
        },
        onMessage: (envelope) => get().applyServerEvent(envelope),
      });
      clientInstance = client;
      client.connect();
    },

    disconnect: () => {
      if (clientInstance) {
        clientInstance.disconnect();
        clientInstance = null;
      }
      set({ connectionState: 'disconnected' });
    },

    send: (envelope) => {
      clientInstance?.send(envelope);
    },

    applyServerEvent: (envelope: ServerEnvelope) => {
      const receivedAt = Date.now();
      set((prev) => {
        const next = applyEvent(prev, envelope);
        const resetMuteHold = next.call.phase === 'ended' || next.call.phase === 'offered';
        return {
          ...next,
          ...(resetMuteHold
            ? {
                callMuted: false,
                callHeld: false,
                voiceConnectionState: 'disconnected' as const,
              }
            : {}),
        };
      });
      const occurredAt = new Date(envelope.occurred_at).getTime();
      if (Number.isFinite(occurredAt)) {
        const lagMs = receivedAt - occurredAt;
        getMetricsCollector().reportEventLag(envelope.event_type, lagMs);
        if (envelope.event_type === 'hint_generated') {
          getMetricsCollector().reportHintLatency(lagMs);
          getMetricsCollector().reportHintReceived();
        }
      }
    },

    setCustomerCard: (card) => {
      set((prev) => ({
        workspace: {
          ...prev.workspace,
          customerCard: card,
          ...(card && {
            customerId: card.clientId,
            loadedAt: prev.workspace.loadedAt ?? new Date().toISOString(),
          }),
          ...(card === null && { customerId: null, loadedAt: null }),
        },
      }));
    },

    setScenarioField: (field, value, source) => {
      set((prev) => ({
        workspace: {
          ...prev.workspace,
          scenarioFields: {
            ...prev.workspace.scenarioFields,
            [field]: { value, source },
          },
        },
      }));
    },

    setWrapUpOutcome: (outcome) => {
      set((prev) => ({
        workspace: {
          ...prev.workspace,
          wrapUpOutcome: outcome,
        },
      }));
    },

    dismissWrapUp: () => {
      set((prev) => ({
        call: initialCallState,
        workspace: {
          ...prev.workspace,
          wrapUpRequiredFields: [],
          wrapUpOutcome: null,
        },
      }));
    },

    clearMeetingConflict: () => {
      set((prev) => ({
        workspace: {
          ...prev.workspace,
          meetingConflict: null,
        },
      }));
    },

    confirmMeeting: () => {
      set((prev) => ({
        workspace: {
          ...prev.workspace,
          meetingConflict: null,
          meetingConfirmedAt: new Date().toISOString(),
        },
      }));
    },

    simulateWrapUpState: (params) => {
      const {
        callId,
        endReason = 'operator_end',
        durationMs = 120000,
        requiredFields = ['date', 'time', 'address'],
      } = params;
      set((prev) => ({
        call: {
          ...initialCallState,
          phase: 'ended',
          callId,
          sessionId: `session-${callId}`,
          endedAt: new Date().toISOString(),
          durationMs,
          endReason,
        },
        workspace: {
          ...prev.workspace,
          wrapUpRequiredFields: requiredFields,
        },
      }));
    },

    simulateMeetingConflict: (params) => {
      const { message = translateCurrent('demo.slot_busy'), alternatives = ['14:00', '16:00'] } =
        params;
      set((prev) => ({
        workspace: {
          ...prev.workspace,
          meetingConflict: {
            code: 'slot_busy',
            message,
            alternatives,
          },
          meetingConfirmedAt: null,
        },
      }));
    },

    setAddressCoords: (coords) => {
      set((prev) => ({
        workspace: {
          ...prev.workspace,
          addressCoords: coords,
        },
      }));
    },

    clearAddressWarning: () => {
      set((prev) => ({
        workspace: {
          ...prev.workspace,
          addressWarning: null,
        },
      }));
    },

    simulateAddressWarning: (params) => {
      const { code = 'ambiguous', message = translateCurrent('demo.clarify_address') } = params;
      set((prev) => ({
        workspace: {
          ...prev.workspace,
          addressWarning: { code, message },
        },
      }));
    },

    simulateCopilotEvent: (eventType, payload) => {
      const eventId = `demo-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      set((prev) => ({
        copilot: reduceCopilot(prev.copilot, eventType, payload, eventId),
      }));
    },

    markHintUsed: (hintId) => {
      getMetricsCollector().reportHintMarkedUsed();
      get().send(buildHintMarkUsed(hintId));
      const callId = get().call.callId;
      if (callId) {
        sendFeedback({
          call_id: callId,
          session_id: get().call.sessionId ?? undefined,
          type: 'hint_acknowledged',
          hint: hintId,
        }).catch(() => {});
      }
      set((prev) => ({
        copilot: {
          ...prev.copilot,
          dismissedHintIds: [...prev.copilot.dismissedHintIds, hintId],
        },
      }));
    },

    markHintIrrelevant: (hintId) => {
      getMetricsCollector().reportHintMarkedIrrelevant();
      get().send(buildHintMarkIrrelevant(hintId));
      set((prev) => ({
        copilot: {
          ...prev.copilot,
          dismissedHintIds: [...prev.copilot.dismissedHintIds, hintId],
        },
      }));
    },

    clearHighlight: () => {
      set((prev) => ({
        copilot: { ...prev.copilot, highlight: null },
      }));
    },

    simulateHighlight: (params) => {
      const {
        target_widget,
        level = 'soft',
        reason = translateCurrent('demo.ai_recommendation'),
        ttl_ms = 8000,
      } = params;
      const eventId = `demo-hl-${Date.now()}`;
      set((prev) => ({
        copilot: reduceCopilot(
          prev.copilot,
          'highlight_recommended',
          {
            target_widget,
            level,
            reason,
            ttl_ms,
          },
          eventId
        ),
      }));
    },

    setCallMuted: (muted) => {
      set({ callMuted: muted });
    },

    setCallHeld: (held) => {
      set({ callHeld: held });
    },

    setVoiceConnectionState: (state) => {
      set({ voiceConnectionState: state });
    },

    reset: () => {
      get().disconnect();
      set({ ...getInitialOperatorState(), connectionState: 'disconnected' });
    },
  }));
}

export const operatorStore = createOperatorStore();
